import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

interface AdminLayoutState {
  sidebarOpen: boolean;
}

interface AdminLayoutContextType extends AdminLayoutState {
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
}

const AdminLayoutContext = createContext<AdminLayoutContextType | undefined>(undefined);

interface AdminLayoutProviderProps {
  children: ReactNode;
}

export function AdminLayoutProvider({ children }: AdminLayoutProviderProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const openSidebar = useCallback(() => setSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleSidebar = useCallback(() => setSidebarOpen((prev) => !prev), []);

  return (
    <AdminLayoutContext.Provider
      value={{
        sidebarOpen,
        openSidebar,
        closeSidebar,
        toggleSidebar,
      }}
    >
      {children}
    </AdminLayoutContext.Provider>
  );
}

/**
 * Hook to access admin layout context
 * Only available within AdminLayout
 */
export function useAdminLayout(): AdminLayoutContextType {
  const context = useContext(AdminLayoutContext);
  if (context === undefined) {
    throw new Error('useAdminLayout must be used within AdminLayoutProvider');
  }
  return context;
}
